import { createSlice } from '@reduxjs/toolkit';
import { 
  getSchools, 
  getSchoolById, 
  getSchoolByCode,
  createSchool, 
  updateSchool, 
  deleteSchoolById 
} from '../auth/api';

const schoolSlice = createSlice({
  name: 'school',
  initialState: {
    list: [],
    selectedSchool: null,
    selectedSchoolId: null,
    loading: false,
    error: null,
  },
  reducers: {
    setLoading: (state) => {
      state.loading = true;
      state.error = null;
    },
    setSchools: (state, action) => {
      state.loading = false;
      state.error = null;
      state.list = action.payload;
    },
    setSelectedSchool: (state, action) => {
      state.loading = false;
      state.error = null;
      state.selectedSchool = action.payload;
    },
    setSelectedSchoolId: (state, action) => {
      state.selectedSchoolId = action.payload;
    },
    createSchoolSuccess: (state, action) => {
      state.list.push(action.payload.school);
      state.loading = false;
      state.error = null;
    },
    updateSchoolSuccess: (state, action) => {
      const { schoolId, updatedSchool } = action.payload;
      const updatedList = state.list.map((school) => {
        if (school._id === schoolId) {
          return updatedSchool;
        }
        return school;
      });
      state.list = updatedList;
      state.loading = false;
      state.error = null;
    },
    deleteSchoolSuccess: (state, action) => {
      state.list = state.list.filter((school) => school._id !== action.payload);
      state.loading = false;
      state.error = null;
    },
    setError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const { 
  setLoading, 
  setSchools, 
  setSelectedSchool,
  setSelectedSchoolId,
  createSchoolSuccess, 
  updateSchoolSuccess, 
  deleteSchoolSuccess, 
  setError 
} = schoolSlice.actions;

// Async thunk to fetch all schools
export const fetchSchoolsAsync = () => async (dispatch) => {
  try {
    dispatch(setLoading());
    const response = await getSchools();
    const schools = response.schools || [];
    // Filter out deleted schools
    const filteredSchools = schools.filter(school => school.deleted !== true);
    dispatch(setSchools(filteredSchools));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

// Async thunk to fetch school by ID
export const fetchSchoolByIdAsync = (schoolId) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const school = await getSchoolById(schoolId);
    dispatch(setSelectedSchool(school));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

// Async thunk to fetch school by code
export const fetchSchoolByCodeAsync = (code) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const school = await getSchoolByCode(code);
    dispatch(setSelectedSchool(school));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

// Async thunk to create a new school
export const createSchoolAsync = (schoolData) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const response = await createSchool(schoolData);
    dispatch(createSchoolSuccess(response));
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

// Async thunk to update a school
export const updateSchoolAsync = (schoolId, schoolData) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const response = await updateSchool(schoolId, schoolData);
    dispatch(updateSchoolSuccess({ 
      schoolId, 
      updatedSchool: response.school 
    }));
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

// Async thunk to delete a school
export const deleteSchoolAsync = (schoolId) => async (dispatch) => {
  try {
    dispatch(setLoading());
    await deleteSchoolById(schoolId);
    dispatch(deleteSchoolSuccess(schoolId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export default schoolSlice.reducer;
